/**
 * <cw-product>: the product page (design-handoff/screens/pdp.md, components/pdp-parts.md;
 * Shopping v13 view7-view14, ported).
 *
 * - Choices: family, form and grind chips exactly as the options drawer (cw-picker.js); the
 *   packs are cards. Tap a card to select that pack; the ADD row and the bar follow the
 *   selection and add / step THAT variant. Family chips open the member's own page.
 * - Pack cards (v13 view13/14): the per-kg rate for atta, millets, dals, rice, oils and ghee;
 *   "Save ₹X" band under a card that beats the first pack's unit price by ₹5+; "Best value" on
 *   the best unit price when it is 3%+ better (and not the product's own tag); the in-basket
 *   badge; all cards one width (96-118, wide enough for the widest price + MRP).
 * - Bar: always on phones. At 600+ it is hidden while the ADD row is in view and slides in
 *   once the row has scrolled out (tablet: it then replaces the floating basket bar).
 * - Gallery: left / right thirds step through the photos, dots jump; back, favourite (kept in
 *   this browser), share (the phone's share sheet, else the link is copied).
 * - Facts: "Packed · After your order"; "Delivery · " Flow's promise for the chosen address.
 * - The fresh line is the product's own words (custom.fresh_line). The prototype's per-aisle
 *   lines were dropped by the owner (1 Oct): each product will get its own; until then, no card.
 */
import { cart, MAX_PER_LINE } from '@cw/cart';
import { CwSheet } from '@cw/sheet';
import { view as deliveryView } from '@cw/delivery';
import { paintNext } from '@cw/aisles';
import { esc, rupees, baseOf, rateOf, packLabel, productData, configOf } from '@cw/products';
import { selOf, setSel, rowsFor, ribbonOf, familyOf, groupsHtml, paintGroups, chipAction } from '@cw/picker';

const RATE_AISLES = ['atta', 'millets', 'dal', 'rice', 'oils', 'ghee'];
const FAV_KEY = 'cw:fav';
const WIDE = window.matchMedia('(min-width: 600px)');

const readFavs = () => {
  try {
    return JSON.parse(localStorage.getItem(FAV_KEY) || '{}') || {};
  } catch {
    return {};
  }
};

let measure = null;
/** v13 textW(): the width of a text in a font, for the card width. */
function textW(text, font) {
  measure = measure || document.createElement('canvas').getContext('2d');
  measure.font = font;
  return measure.measureText(String(text || '')).width;
}

class CwProduct extends HTMLElement {
  #m = null;
  #family = null;
  #icons = {};
  #shot = 0;
  #observer = null;

  connectedCallback() {
    this.#m = productData(this.dataset.productId);
    if (!this.#m) return;
    this.#family = familyOf(this.#m);
    const icons = document.querySelector('cw-options template[data-cw="icons"]');
    for (const el of icons?.content.querySelectorAll('[data-icon]') || []) this.#icons[el.dataset.icon] = el.innerHTML.trim();
    this.scroller = this.querySelector('[data-cw="scroller"]');
    this.addrow = this.querySelector('[data-cw="addrow"]');
    this.bar = this.querySelector('[data-cw="bar"]');

    this.#paintStatic();
    this.#paint();
    this.#paintDelivery();
    paintNext(this.querySelector('[data-cw="next"]'), this.dataset.aisle);

    this.addEventListener('click', this.#onClick);
    document.addEventListener('cw:cart:change', this.#onCart);
    document.addEventListener('cw:delivery:change', this.#paintDelivery);
    WIDE.addEventListener('change', this.#watchRow);
    this.#watchRow();
    // Phones: no delivery row on the product page, so the rail carries the search (v13).
    if (!WIDE.matches) document.dispatchEvent(new CustomEvent('cw:fold', { detail: { folded: true, navHidden: true } }));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('cw:cart:change', this.#onCart);
    document.removeEventListener('cw:delivery:change', this.#paintDelivery);
    WIDE.removeEventListener('change', this.#watchRow);
    this.#observer?.disconnect();
  }

  /** Claims, badge, fresh line, favourite, choices markup. */
  #paintStatic() {
    const m = this.#m;
    const claims = Array.isArray(m.claims) ? m.claims.filter((c) => c && (c.title || c.sub)) : [];
    const box = this.querySelector('[data-cw="claims"]');
    if (claims.length) {
      box.innerHTML =
        m.aisle === 'spices' && !m.cardChip
          ? `<div class="cw-pdp__cues">${claims.map((c) => `<span class="cw-opt__cue">${esc(c.title)}</span>`).join('')}</div>`
          : `<div class="cw-pdp__tiles">${claims
              .slice(0, 3)
              .map((c) => `<div class="cw-pdp__tile"><div class="cw-pdp__tile-a cw-opt-clamp2">${esc(c.title)}</div><div class="cw-pdp__tile-b cw-opt-clamp2">${esc(c.sub)}</div></div>`)
              .join('')}</div>`;
    } else box.hidden = true;

    const ribbon = ribbonOf(m);
    const badge = this.querySelector('[data-cw="badge"]');
    if (ribbon) {
      badge.textContent = ribbon.charAt(0) + ribbon.slice(1).toLowerCase();
      badge.hidden = false;
    }
    // The fresh line is the product's own (custom.fresh_line); none written yet, no card.
    const fresh = this.querySelector('.cw-pdp__fresh');
    if (m.fresh) this.querySelector('[data-cw="fresh"]').textContent = m.fresh;
    else fresh.hidden = true;

    const fav = !!readFavs()[m.id];
    const favButton = this.querySelector('[data-cw="fav"]');
    favButton.classList.toggle('is-on', fav);
    favButton.setAttribute('aria-pressed', String(fav));

    this.querySelector('[data-cw="groups"]').innerHTML = groupsHtml(m, this.#family, this.#icons);
  }

  /** Chips, cards, the ADD row and the bar for the selection and the basket. */
  #paint() {
    const m = this.#m;
    const sel = paintGroups(this.querySelector('[data-cw="groups"]'), m, this.#family);
    const rows = rowsFor(m, sel);
    const current = rows.find((v) => v.pack === sel.pack) || rows[0];
    const useRate = RATE_AISLES.includes(m.aisle);

    // v13 view13: best unit price, the product's own tag, saving bands.
    const per = rows.map((v) => {
      const b = baseOf(v.label);
      return b ? v.price / b : null;
    });
    let best = -1;
    let pct = 0;
    if (useRate && per[0]) {
      per.forEach((r, i) => {
        if (i > 0 && r && rows[i].available) {
          const d = (per[0] - r) / per[0];
          if (d > pct) {
            pct = d;
            best = i;
          }
        }
      });
    }
    if (pct < 0.03) best = -1;
    const own = m.packTag && m.packTag.label ? m.packTag : null;
    const popIdx = own ? rows.findIndex((v) => String(v.id) === String(own.variant) || v.label === packLabel(own.variant) || v.pack === own.variant) : -1;
    const popLabel = own ? (own.label === 'Most bought' ? 'Popular' : own.label) : '';
    const bvIdx = best >= 0 && best !== popIdx ? best : -1;
    const anyTag = popIdx >= 0 || bvIdx >= 0;
    const b0 = rows[0] ? baseOf(rows[0].label) : null;
    const saveOf = (v, i) => {
      const b = baseOf(v.label);
      return v.available && i > 0 && b && b0 ? Math.round(((rows[0].price / b0) * b - v.price) / 100) : 0;
    };
    const anySave = rows.some((v, i) => saveOf(v, i) >= 5);
    const rateSlot = useRate && per.some(Boolean);
    const minimal = !anyTag && !rateSlot;
    const width = Math.min(
      118,
      Math.max(
        96,
        ...rows.map((v) => {
          if (!v.available) return 0;
          const mrp = v.compare > v.price ? 5 + textW(rupees(v.compare), "500 10px 'Figtree'") : 0;
          return Math.ceil(textW(rupees(v.price), "800 13px 'Figtree'") + mrp + 21);
        })
      )
    );

    const name = this.#family?.head === 'Pressing' ? this.#family.title : m.title;
    const cards = this.querySelector('[data-cw="cards"]');
    cards.style.setProperty('--cw-card-w', `${width}px`);
    cards.innerHTML = rows
      .map((v, i) => {
        const on = v === current;
        const q = cart.qtyOf(v.id);
        const save = saveOf(v, i);
        const tag = i === popIdx ? popLabel : i === bvIdx ? 'Best value' : '';
        const parts = v.label.match(/^([\d.,/]+)\s*(.*)$/);
        const rate = useRate ? rateOf(v.label, v.price).replace(' / ', '/') : '';
        return `<div class="cw-pdp__cardcol${save >= 5 ? ' has-save' : ''}">
            <button type="button" class="cw-pdp__card${on ? ' is-on' : ''}${anyTag ? ' has-tags' : ''}${minimal ? ' is-minimal' : ''}" data-pack="${esc(v.pack)}" aria-pressed="${on}" aria-label="${esc(`${name} ${v.label}`)}">
              ${tag ? `<span class="cw-pdp__tag">${esc(tag)}</span>` : ''}
              ${q > 0 && v.available ? `<span class="cw-pdp__q">${q}</span>` : ''}
              <span class="cw-pdp__size"><span class="cw-pdp__num">${esc(parts ? parts[1] : v.label)}</span><span class="cw-pdp__unit">${esc(parts ? parts[2] : '')}</span></span>
              <span class="cw-pdp__grow"></span>
              <span class="cw-pdp__rule"></span>
              ${
                v.available
                  ? `<span class="cw-pdp__pricerow"><span class="cw-pdp__cprice">${rupees(v.price)}</span>${v.compare > v.price ? `<span class="cw-pdp__cmrp"><span class="cw-sr">MRP </span>${rupees(v.compare)}</span>` : ''}</span>`
                  : '<span class="cw-pdp__sold">Sold out</span>'
              }
              ${rateSlot ? `<span class="cw-pdp__rate">${esc(v.available ? rate : '')}</span>` : ''}
            </button>
            ${save >= 5 ? `<button type="button" class="cw-pdp__saveband" data-pack="${esc(v.pack)}" tabindex="-1">Save ₹${save.toLocaleString('en-IN')}</button>` : anySave ? '<span class="cw-pdp__bandgap"></span>' : ''}
          </div>`;
      })
      .join('');

    // The ADD row and the bar: the selected pack.
    if (!current) return;
    const rateText = rateSlot && current.available ? ` · ${rateOf(current.label, current.price).replace(' / ', '/')}` : '';
    const label = `${configOf(m, current)}${rateText}`;
    const q = cart.qtyOf(current.id);
    const what = `${name} ${current.label}`;
    let control;
    if (!current.available) control = '<span class="cw-pdp__soldout">SOLD OUT</span>';
    else if (q > 0)
      control = `<div class="cw-pdp__step">
          <button type="button" class="cw-pdp__pm" data-variant="${current.id}" data-step="-1" aria-label="Remove one ${esc(what)}">${this.#icons.minus || '−'}</button>
          <span class="cw-pdp__stepq" aria-live="polite">${q}</span>
          <button type="button" class="cw-pdp__pm" data-variant="${current.id}" data-step="1" aria-label="Add one ${esc(what)}"${q >= MAX_PER_LINE ? ' disabled' : ''}>${this.#icons.plus || '+'}</button>
        </div>`;
    else control = `<button type="button" class="cw-pdp__add" data-variant="${current.id}" data-step="1" aria-label="Add ${esc(what)}">ADD</button>`;
    for (const scope of [this.addrow, this.bar]) {
      if (!scope) continue;
      scope.querySelector('[data-cw="sel-label"]').textContent = label;
      scope.querySelector('[data-cw="sel-price"]').textContent = rupees(current.price);
      const mrp = scope.querySelector('[data-cw="sel-mrp"]');
      mrp.textContent = current.compare > current.price ? rupees(current.compare) : '';
      mrp.hidden = !(current.compare > current.price);
      scope.querySelector('[data-cw="ctl"]').innerHTML = control;
    }
    const n = this.querySelector('[data-cw="basket-n"]');
    if (n) {
      n.hidden = cart.count === 0;
      n.textContent = String(cart.count);
    }

  }

  /** "Delivery · Today before 3pm" once Flow has answered for the chosen address. */
  #paintDelivery = () => {
    const when = this.querySelector('[data-cw="fact-when"]');
    if (!when) return;
    const v = deliveryView();
    const a = v.answer;
    if (a && a.spot !== 'origin' && a.serviceable === false) when.textContent = `Pick up · ${a.pickup?.when || 'from our shop'}`;
    else if (a?.standard?.when) when.textContent = a.standard.when;
  };

  /** 600+: the bar shows once the ADD row has scrolled out of view. */
  #watchRow = () => {
    this.#observer?.disconnect();
    this.classList.remove('is-bar');
    document.dispatchEvent(new CustomEvent('cw:pdp:bar', { detail: { shown: false } }));
    if (!WIDE.matches || !this.addrow || !this.scroller) return;
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        const out = !entry.isIntersecting && entry.boundingClientRect.bottom < (entry.rootBounds?.top ?? 0) + 4;
        this.classList.toggle('is-bar', out);
        document.dispatchEvent(new CustomEvent('cw:pdp:bar', { detail: { shown: out } }));
      },
      { root: this.scroller, threshold: 0 }
    );
    this.#observer.observe(this.addrow);
  };

  #onCart = () => this.#paint();

  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const m = this.#m;

    const chip = chipAction(target, m);
    if (chip?.member) {
      const next = productData(chip.member);
      if (next?.url) window.location.href = next.url;
      return;
    }
    if (chip?.sel) {
      setSel(m, chip.sel);
      return this.#paint();
    }

    const pack = target.closest('[data-pack]');
    if (pack) {
      setSel(m, { pack: pack.dataset.pack });
      return this.#paint();
    }

    const step = target.closest('[data-step]');
    if (step && step.dataset.variant) {
      if (navigator.onLine === false) return document.dispatchEvent(new CustomEvent('cw:offline'));
      const q = cart.qtyOf(step.dataset.variant) + Number(step.dataset.step);
      if (q > MAX_PER_LINE) return;
      cart.setQty(step.dataset.variant, Math.max(0, q), this, { productId: m.id });
      return;
    }

    const shotStep = target.closest('[data-shot-step]');
    if (shotStep) return this.#showShot(this.#shot + Number(shotStep.dataset.shotStep));
    const shotTo = target.closest('[data-shot-to]');
    if (shotTo) return this.#showShot(Number(shotTo.dataset.shotTo));

    if (target.closest('[data-cw="fav"]')) return this.#toggleFav();
    if (target.closest('[data-cw="share"]')) return this.#share();
    if (target.closest('[data-open-details]')) {
      this.querySelector('cw-details')?.open(target.closest('button'));
      return;
    }

    const back = target.closest('[data-cw="back"]');
    if (back && document.referrer.startsWith(window.location.origin) && window.history.length > 1) {
      event.preventDefault();
      window.history.back();
      return;
    }

    const rel = target.closest('[data-rel-step]');
    if (rel) {
      const row = this.querySelector('[data-cw="rel"]');
      row?.scrollBy({ left: Number(rel.dataset.relStep) * (row.clientWidth - 40), behavior: 'smooth' });
    }
  };

  #showShot(i) {
    const shots = [...this.querySelectorAll('[data-cw="hero"] .cw-pdp__shot')];
    if (shots.length < 2) return;
    this.#shot = (i + shots.length) % shots.length;
    shots.forEach((s, k) => s.classList.toggle('is-on', k === this.#shot));
    this.querySelectorAll('.cw-pdp__dot').forEach((d, k) => d.classList.toggle('is-on', k === this.#shot));
    const img = shots[this.#shot];
    if (img.loading === 'lazy') img.loading = 'eager';
  }

  #toggleFav() {
    const favs = readFavs();
    const id = this.#m.id;
    if (favs[id]) delete favs[id];
    else favs[id] = true;
    try {
      localStorage.setItem(FAV_KEY, JSON.stringify(favs));
    } catch {
      // kept for this page only
    }
    const on = !!favs[id];
    const button = this.querySelector('[data-cw="fav"]');
    button.classList.toggle('is-on', on);
    button.setAttribute('aria-pressed', String(on));
  }

  async #share() {
    const url = window.location.href.split('?')[0];
    const title = this.querySelector('.cw-pdp__title')?.textContent || document.title;
    try {
      if (navigator.share) await navigator.share({ title, url });
      else {
        await navigator.clipboard.writeText(url);
        document.dispatchEvent(new CustomEvent('cw:toast', { detail: { text: 'Link copied' } }));
      }
    } catch {
      // the shopper closed the share sheet
    }
  }
}

/** The product's Details sheet / 420 panel. */
class CwDetails extends CwSheet {}

if (!customElements.get('cw-details')) customElements.define('cw-details', CwDetails);
if (!customElements.get('cw-product')) customElements.define('cw-product', CwProduct);
